<script lang="ts" setup>
	const props = defineProps<{
		gadgetType?: string
	}>()

	const stepCount = shallowRef(0)
	const images: Record<string, { default: string }> = import.meta.glob('~/assets/images/png/*.png', { eager: true })

	defineEmits<{
		(event: 'proceed'): void
	}>()
	const mobileSource = computed(() => ({
		'front view': {
			src: images['/assets/images/png/mobile-front.png']?.default ?? ''
		},
		'Back view': {
			src: images['/assets/images/png/mobile-back.png']?.default ?? ''
		},
		'Phone turned on': {
			src: images['/assets/images/png/mobile-on.png']?.default ?? ''
		},
		'Serial number of phone': {
			src: images['/assets/images/png/mobile-serial.png']?.default ?? ''
		}
	}))
	const laptopSource = computed(() => ({
		'Top view': {
			src: images['/assets/images/png/laptop-top-view.png']?.default ?? ''
		},
		'Bottom view': {
			src: images['/assets/images/png/laptop-bottom-view.png']?.default ?? ''
		},
		'Serial no. on screen': {
			src: images['/assets/images/png/laptop-front-view.png']?.default ?? ''
		},
		'Serial no. on laptop body': {
			src: images['/assets/images/png/laptop-body.png']?.default ?? ''
		}
	}))
	const isLaptop = computed(() => {
		return props.gadgetType?.toLowerCase() === 'laptop'
	})
	const processSteps = ref<
		{
			label: string
			key?: string
			active?: boolean
			imgSrc?: string
			notice?: {
				label: string
				title: string
				info: string[]
			}
		}[]
	>([
		{
			label: `Capture the ${isLaptop.value ? 'top' : 'front'}  view of your gadget`,
			imgSrc: (isLaptop.value && laptopSource.value['Top view'].src) || mobileSource.value['front view'].src,
			active: true
		},
		{
			label: `Capture the ${isLaptop.value ? 'bottom' : 'Back'} view of your gadget`,
			imgSrc: (isLaptop.value && laptopSource.value['Bottom view'].src) || mobileSource.value['Back view'].src,
			active: false
		},
		{
			label: 'Capture the Serial no. on screen of gadget',
			key: 'serial_number',
			imgSrc:
				(isLaptop.value && laptopSource.value['Serial no. on laptop body'].src) ||
				mobileSource.value['Serial number of phone'].src,
			active: false,
			notice: {
				label: `Position of serial number varies based on the type of your ${props.gadgetType}`,
				title: '',
				info: [
					'Sticker at the back of the phone or laptop',
					'The battery compartment, if the battery is removable',
					'The phone or laptop settings using the "about" and find the “serial number”.'
				]
			}
		}
	])

	const activeSteps = computed(() => {
		return processSteps.value.find(item => item.active)
	})
	const nextStep = (index = 0) => {
		processSteps.value.forEach(item => {
			item.active = false
		})

		if (index > 0) {
			stepCount.value = index - 1
			const targetStep = processSteps.value[index - 1]
			if (targetStep) {
				targetStep.active = true
			}
			return null
		}
		if (stepCount.value < processSteps.value.length - 1) {
			stepCount.value++
		}
		const targetStep = processSteps.value[stepCount.value]
		if (targetStep) {
			targetStep.active = true
		}
	}
</script>

<template>
	<div class="w-full">
		<div class="flex w-full flex-col items-center justify-center">
			<ul
				class="mt-12 inline-flex min-h-[40px] items-center gap-x-3 overflow-x-auto rounded-[20px] border border-purple-300 bg-purple-100 px-3 py-1"
			>
				<li
					v-for="(item, index) in processSteps"
					:key="index"
					class="inline-flex h-[24px] w-[30px] flex-none items-center justify-center rounded-[20px] text-center text-sm"
					:class="{
						'bg-purple-500 text-white': item.active,
						'text-gray-600': !item.active
					}"
					@click="nextStep(index + 1)"
				>
					{{ index + 1 }}
				</li>
			</ul>

			<div class="mt-12 w-full">
				<span
					class="list-item list-inside list-disc text-center text-sm font-medium tracking-wide text-gray-600"
				>
					{{ activeSteps?.label || '---' }}
				</span>

				<div
					class="mx-auto mt-10 flex max-h-[400px] min-h-[223px] w-full flex-col justify-between overflow-hidden rounded-lg bg-purple-50 px-3 pb-0 pt-5 sm:max-h-[300px] sm:max-w-[336px]"
				>
					<span class="mb-5 text-center font-normal text-gray-500">
						Ensure that this is clearly captured.
					</span>

					<img
						:src="activeSteps?.imgSrc || ''"
						loading="lazy"
						:alt="activeSteps?.label || 'Gadget image display'"
					/>
				</div>
				<div
					v-show="activeSteps?.key === 'serial_number'"
					class="mx-auto mt-5 w-full rounded-lg bg-primary-50 px-5 py-2 sm:max-w-[331px] sm:p-5"
				>
					<p class="py-5 text-xs font-medium leading-snug tracking-wide text-gray-600">
						<span class="text-warning-500">Note:</span>
						{{ activeSteps?.notice?.label }}
						<br />
						<br />
						You can locate the serial number through the following:
					</p>

					<ul class="list-outside list-disc pl-2">
						<li
							v-for="(info, index) in activeSteps?.notice?.info"
							:key="`serial-info-` + index"
							class="list-item pt-2 text-[10px] font-medium tracking-wide text-purple-500"
						>
							{{ info }}
						</li>
					</ul>
				</div>
			</div>

			<div class="mt-5 flex w-full flex-col justify-center space-y-1">
				<button
					v-if="stepCount < processSteps.length - 1"
					type="button"
					class="default-btn justify-center bg-primary-2 !font-medium text-white"
					@click="() => nextStep()"
				>
					Next
				</button>
				<button
					v-else
					type="button"
					class="default-btn justify-center bg-primary-2 !font-medium text-white"
					@click="$emit('proceed')"
				>
					Proceed
				</button>
				<button
					type="button"
					class="default-btn justify-center !border-none !font-medium text-primary-500"
					@click="() => nextStep(processSteps.length)"
				>
					Skip
				</button>
			</div>
		</div>
	</div>
</template>
